import React, { ReactNode } from 'react';
import DataCard from './DataCard';

export interface ResponsiveTableColumn<T> {
    key: string;
    label: string;
    render: (row: T) => ReactNode;
    hideOn?: 'mobile';
    align?: 'left' | 'right' | 'center';
    className?: string;
}

export interface ResponsiveTableProps<T> {
    columns: ResponsiveTableColumn<T>[];
    data: T[];
    keyExtractor: (row: T) => string | number;
    cardRender?: (row: T) => ReactNode;
    emptyMessage?: string;
    rowClassName?: (row: T) => string;
    onRowClick?: (row: T) => void;
}

function alignClass(align?: 'left' | 'right' | 'center'): string {
    if (align === 'right') return 'text-right';
    if (align === 'center') return 'text-center';
    return 'text-left';
}

function ResponsiveTable<T>({
    columns,
    data,
    keyExtractor,
    cardRender,
    emptyMessage = 'Nessun risultato.',
    rowClassName,
    onRowClick,
}: ResponsiveTableProps<T>) {
    if (data.length === 0) {
        return (
            <div className="text-center py-16 text-gray-500">
                {emptyMessage}
            </div>
        );
    }

    const mobileColumns = columns.filter(c => c.hideOn !== 'mobile');

    return (
        <div className="w-full">
            {/* Mobile / Tablet: card list */}
            <div className="lg:hidden flex flex-col gap-3 p-3">
                {data.map(row => {
                    const key = keyExtractor(row);
                    if (cardRender) {
                        return <React.Fragment key={key}>{cardRender(row)}</React.Fragment>;
                    }
                    const [titleCol, ...rest] = mobileColumns;
                    return (
                        <DataCard
                            key={key}
                            title={titleCol ? titleCol.render(row) : null}
                            fields={rest.map(c => ({ label: c.label, value: c.render(row) }))}
                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                        />
                    );
                })}
            </div>

            {/* Desktop: table */}
            <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs uppercase font-bold tracking-wider">
                        <tr>
                            {columns.map(c => (
                                <th key={c.key} className={`p-4 ${alignClass(c.align)} ${c.className ?? ''}`}>
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {data.map(row => {
                            const key = keyExtractor(row);
                            const rowCls = rowClassName ? rowClassName(row) : '';
                            const clickable = !!onRowClick;
                            return (
                                <tr
                                    key={key}
                                    onClick={clickable ? () => onRowClick!(row) : undefined}
                                    className={`transition-colors ${clickable ? 'cursor-pointer hover:bg-blue-50/30' : ''} ${rowCls}`}
                                >
                                    {columns.map(c => (
                                        <td key={c.key} className={`p-4 ${alignClass(c.align)} ${c.className ?? ''}`}>
                                            {c.render(row)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ResponsiveTable;
